import React from 'react';
import PortfolioDemo from './demos/PortfolioDemo';
import ScreenerDemo from './demos/ScreenerDemo';
import MarketTrendsDemo from './demos/MarketTrendsDemo';
import MobileDemoGate from './demos/MobileDemoGate';
import FAQAccordion from './FAQAccordion';
import BackToTop from './BackToTop';
import './MarketingPage.css';
import WalkthroughTour from './WalkthroughTour';
import HeroGradeWidget from './HeroGradeWidget';

const MARKETING_FAQS = [
  {
    question: 'What is AlphaGrade?',
    summary:
      "AlphaGrade is an investment research platform built for people who manage their own money. Institutional-grade data + an AI thinking partner + transparent scoring. It's a tool — not financial advice. Every decision is yours.",
    answer:
      "AlphaGrade is an investment research platform built for people who manage their own money but want institutional-grade tools to back their decisions. We pay for the same data feeds institutional investors use — analyst price targets and rating changes, insider transactions, Congressional trades, 13F filings, earnings transcripts, short interest, options flow — and wire it all into an AI thinking partner that knows your portfolio, your risk tolerance, and your investment style.\n\nEvery stock, ETF, mutual fund, or crypto gets graded on a 0-100 scale across multiple research lenses (fundamentals, technicals, asymmetric upside, macro conditions), and every score breaks down into the underlying tests so you can see exactly why something looks good or bad. No black boxes.\n\n**AlphaGrade is a tool, not financial advice.** We don't tell you what to buy. We give you organized data, transparent scoring, and an AI thinking partner so you can do your own research more efficiently. You — and only you — are responsible for your investment decisions. Always do your own research and consider consulting a licensed financial professional before acting on anything you see here.",
  },
  {
    question: 'Who is AlphaGrade for?',
    answer:
      "AlphaGrade is built for **people who manage their own money** — successful professionals, business owners, and serious DIY investors who don't want to pay an advisor fees to do what they can do themselves, but also don't want to wing it on a brokerage account without understanding what makes a stock a good or bad purchase.\n\nWhether you've been investing for 20 years or you're stepping off the sideline for the first time, the same scoring system meets you where you are. Beginners get plain-English explanations, a built-in glossary of 130+ terms, letter grades, and a guided onboarding tour. More experienced investors get institutional-grade scoring methodology, fund-aware analysis, trade simulation, cross-asset correlation views, and an AI thinking partner that can actually pull live data on demand.\n\nIf you've ever had a great instinct on a stock and not acted because you weren't sure if a savvy investor would think the same way — AlphaGrade is for you.",
  },
  {
    question: 'How is AlphaGrade different from Yahoo Finance, Morningstar, or my brokerage?',
    answer:
      "Three things you won't get from Yahoo Finance, Morningstar, or your brokerage:\n\n**1. Institutional data + AI together.** Bloomberg has the data. ChatGPT has the AI. Neither has both. AlphaGrade pays for the institutional data feeds (analyst rating changes, insider transactions, Congressional trades, 13F filings, earnings transcripts, short interest, options flow) and wires them into an AI thinking partner. So when you ask 'is NVDA's valuation reasonable?' the AI is reasoning from real-time data behind paywalls — not from a frozen 2023 training cutoff.\n\n**2. Scoring, not just data.** Most platforms show you 50 metrics and expect you to decide if a stock looks healthy. AlphaGrade scores them — one Quality score, one Setup score, one Convex score, one Conviction Band — all explained in plain English with the underlying tests transparent. Less guesswork. Clearer signals.\n\n**3. Macro regime awareness.** Great stocks in chaotic markets often break. AlphaGrade factors in market regime (Risk-On / Risk-Off) and turbulence so scoring adjusts automatically when conditions deteriorate.\n\nFinal call is always yours.",
  },
  {
    question: 'How do I make sense of how things are scored — Quality vs Setup vs Convex vs Macro vs Turbulence?',
    summary:
      "**Quality** = the business. **Setup** = the entry timing. **Convex** = asymmetric upside. **Macro Energy** = market-wide conditions. **Turbulence** = market stability (dampens Setup & Convex when unstable). Together they form one Conviction score that summarizes the analysis — but the decision is yours.",
    answer:
      "AlphaGrade uses 5 distinct lenses to evaluate any investment:\n\n**Quality (0–100)** — Measures the business itself: profitability, growth, margins, balance sheet strength, competitive moat. For ETFs and funds: expense ratio, AUM, tracking efficiency. A great business in a bad market often survives; a bad business in a great market eventually breaks.\n\n**Setup (0–100)** — Measures technical positioning: trend, momentum, RSI, moving averages, relative strength, volume. Tells you whether the entry timing looks favorable. A great stock can be a poor buy if it's extended.\n\n**Convex (0–5)** — Measures asymmetric upside potential: catalysts, demand signals, valuation gaps, short-interest squeeze potential. Identifies setups where the upside is mathematically larger than the downside.\n\n**Macro Energy (0–5)** — Measures market-wide conditions: liquidity, credit spreads, breadth, volatility regime. Even great stocks struggle in Risk-Off regimes. This score adjusts position-sizing suggestions based on the overall environment.\n\n**Turbulence (0–100%)** — A meta-signal that detects when markets are unstable: vol spikes, correlation breakdown, breadth deterioration. During turbulent markets, technical setups become unreliable. Turbulence automatically dampens Setup and Convex scores so you aren't getting false confidence from noisy data. Quality is never dampened — strong businesses stay strong.\n\nThe 5 combine into a Conviction Score with a band (Favorable / Neutral / Cautious). **These are summaries of the analysis, not buy/sell instructions.** Use them to focus your research; the final decision is always yours.",
  },
  {
    question: 'How do I maximize my usage of AlphaGrade?',
    summary:
      "Top-down workflow: **Market Trends** → **Market Research** → **Stock Screener** → **Portfolio Analysis** → **Macro Energy**. Use each section as a step in narrowing down ideas you want to research before making your own decision.",
    answer:
      "AlphaGrade is designed to support a complete top-down research workflow:\n\n**1. Start at Market Trends.** Get a feel for what's happening today. Check the heatmap to see which sectors are moving, scan top gainers and losers, watch the index marquee, read recent market news. This is your 'what's the market doing right now?' page.\n\n**2. Move to Market Research.** Take the ideas from Market Trends and dig deeper. Ask the AI Research assistant questions about stocks or sectors you're considering — 'top semiconductor companies,' 'high dividend energy stocks,' 'AI chip competitors.' Get back ticker suggestions you can screen instantly.\n\n**3. Use the Stock Screener.** Type in any tickers (from research, from a friend, from a news article) and get scoring breakdowns for each. This is where individual research happens.\n\n**4. Move to Portfolio Analysis.** Two paths: if you already own positions, upload or enter your holdings to see how each one is grading right now and where your risk concentrations are. If you're building a portfolio, use the Portfolio Builder to test a hypothetical allocation before you commit any real capital — same scoring, no real money at risk.\n\n**5. Visit Macro Energy anytime.** When you want a top-level read on whether the overall environment is supportive or hostile to risk-taking, the Macro Energy page gives you the regime, the energy score, and the turbulence model in one view.\n\nThe flow: Market context → Individual research → Portfolio review → Macro check. You can also jump straight to whichever page fits the question you have. Remember: AlphaGrade organizes the information — you make the call.",
  },
  {
    question: "What's the difference between the Stock Screener and Portfolio Analysis?",
    summary:
      "Same scoring engine, different purpose. **Screener** = research individual ideas. **Portfolio Analysis** = analyze a collection of positions together with weighting, P&L, and risk concentration.",
    answer:
      "They use the **exact same scoring engine** — Quality, Setup, Convex, and Macro Energy all run the same way. A stock that scores 78 in the Screener will score 78 in Portfolio Analysis. The difference is the purpose:\n\n**Stock Screener** is for researching individual ideas. Type in tickers (one or many), see grades side by side, save them to watchlists. There's no notion of position size, P&L, or weighting — just the score for each name.\n\n**Portfolio Analysis** is for analyzing a collection of positions together. Enter your holdings (real or hypothetical) with shares and entry prices, and get everything the Screener gives you plus position-weighted portfolio score, P&L tracking, sector concentration, ETF overlap detection, dividend tracking, risk alerts, conviction alerts, benchmark comparison, and trade simulation.\n\nRule of thumb: Screener = 'How does this stock score?' Portfolio Analysis = 'How is what I own (or plan to own) performing as a whole?' Both are research tools — neither is a recommendation to act.",
  },
  {
    question: 'What can I analyze?',
    answer:
      "Individual stocks, ETFs, mutual funds, and crypto. We support 5,000+ U.S.-listed equities, 500+ ETFs, 70+ popular mutual funds (Vanguard, Fidelity, Schwab, etc.), and 24+ major cryptocurrencies. Just type a ticker.",
  },
  {
    question: 'Do I need to connect my brokerage account?',
    answer:
      "No — and we recommend you don't. AlphaGrade never asks for brokerage credentials. You either type tickers directly into the Screener or upload a simple CSV of your holdings to Portfolio Analysis. Your data stays yours.",
  },
  {
    question: 'How do I install AlphaGrade as an app on my phone?',
    answer:
      "AlphaGrade is a Progressive Web App (PWA), which means you can install it directly to your phone's home screen like a native app — no app store required.\n\n**iPhone (Safari):** Tap the Share button at the bottom → Add to Home Screen → Add. AlphaGrade will appear as an app icon on your home screen.\n\n**Android (Chrome):** Tap the three-dot menu in the top right → Install app (or Add to Home screen) → Install.\n\nOnce installed, AlphaGrade opens in full-screen mode without browser bars and works offline for cached pages.",
  },
  {
    question: 'How much does it cost?',
    answer:
      "There's a free tier with no credit card required: 5 portfolio positions, 5 screens a day, 3 Alpha chats a day, and full Market Trends and Macro Energy access. Paid plans are Core ($10.99/mo), Analyst ($24/mo) and Premium ($65/mo), unlocking more positions and screens, Alpha Workspace, Correlations, dividend tracking, trade simulation, and more. Cancel anytime through Stripe's customer portal.",
  },
  {
    question: 'Is AlphaGrade financial advice?',
    answer:
      "**No.** AlphaGrade is a research and analytics tool — not a financial advisor, not a brokerage, not a registered investment advisor. The scores, grades, and recommendations you see are summaries of quantitative analysis based on publicly available data. They are designed to help you research more efficiently, not to tell you what to buy or sell.\n\n**You are responsible for your own investment decisions.** Always do your own research, understand the risks, and consider consulting a licensed financial professional before acting. Past performance is not indicative of future results. All investments involve risk, including the possible loss of principal.",
  },
];

const FEATURES = [
  {
    icon: '📊',
    title: 'Build & Grade Your Portfolio',
    description: "Build and save your portfolio right in the app (or upload a CSV), then grade every position across Quality, Setup, Convex, and Macro. Because it's saved, Alpha and the correlation maps work off your real holdings.",
    tier: 'Free: 5 positions',
  },
  {
    icon: '🤖',
    title: 'Alpha — Your AI Research Partner',
    description: 'Not a generic chatbot. Alpha knows your saved portfolio and pulls live institutional data on demand — 13F flows, insider trades, earnings-call transcripts, options positioning, even YouTube interviews & podcasts — then remembers your thesis across sessions.',
    tier: 'Free: 3 chats/day · Workspace from Core',
  },
  {
    icon: '🔍',
    title: 'Stock Screener',
    description: 'Enter any ticker and run a comprehensive battery of 34 tests across quality, setup, and convex signals — every score broken down with a thorough explanation of why it scored that way.',
    tier: 'Free: 5 screens/day',
  },
  {
    icon: '🔗',
    title: 'Correlation Maps',
    description: 'See how any stock moves with the market, sectors, and macro — and run your own portfolio against itself to expose hidden concentration: the positions that secretly move together. Cross-asset heatmaps + regime-shift signals.',
    tier: 'Core and up',
  },
  {
    icon: '📈',
    title: 'Market Trends',
    description: 'Real-time market heatmap, moving averages, sector performance, fear & greed index, gainers & losers, crypto, and earnings calendar.',
    tier: 'Free access',
  },
  {
    icon: '⚡',
    title: 'Macro Energy Analysis',
    description: 'Six proprietary tests measuring system liquidity, credit spreads, market breadth, volatility, sentiment, and leadership. Know the market regime.',
    tier: 'Free access',
  },
  {
    icon: '💰',
    title: 'Holdings Insight',
    description: 'Fair value estimates, analyst price targets, consensus ratings, insider buy/sell activity, and dollar P&L — all in one sortable table.',
    tier: 'Core (P&L) · Analyst (full)',
  },
  {
    icon: '🔔',
    title: 'Portfolio Alerts',
    description: 'AlphaGrade watches your holdings and emails you when one breaks from its normal relationship with the market — a regime shift worth a look — so you catch the change without staring at charts all day.',
    tier: 'Premium',
  },
];

const STEPS = [
  {
    number: '01',
    title: 'Upload or Build',
    description: 'Upload a CSV of your holdings or build a portfolio manually. Enter tickers, shares, and entry prices — or let AlphaGrade fill in current prices.',
  },
  {
    number: '02',
    title: 'Analyze',
    description: 'AlphaGrade runs 34 tests across four pillars: Quality (fundamentals), Setup (technicals), Convex (asymmetric upside), and Macro Energy (market conditions).',
  },
  {
    number: '03',
    title: 'Decide',
    description: 'Get a clear read on every position — Favorable, Neutral, or Cautious — backed by conviction scores, fair value estimates, and analyst consensus. The call is always yours.',
  },
];

const TIERS = [
  {
    name: 'Free',
    price: 0,
    features: ['5 portfolio positions', 'Portfolio Builder (5 positions)', '5 screener uses/day', '3 portfolio analyses/month', 'Ask Alpha (3 chats/day)', 'Market Trends access', 'Market Heatmap', 'Macro Energy access'],
    cta: 'Get Started',
    popular: false,
  },
  {
    name: 'Core',
    price: 10.99,
    features: ['15 portfolio positions', 'Portfolio Builder (15 positions)', '50 screener uses/day', '100 portfolio analyses/month', 'Ask Alpha (10 chats/day)', 'Alpha Workspace (5/mo)', 'Correlations (3 tickers/mo)', 'Holdings P&L tracking', 'Grade-change alerts (20)', 'PDF Export', 'Basic Benchmark'],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Analyst',
    price: 24,
    features: ['30 portfolio positions', 'Portfolio Builder (30 positions)', 'Unlimited screener', 'Unlimited portfolio analyses', 'Ask Alpha (25 chats/day)', 'Alpha Workspace (25/mo)', 'Correlations (15 tickers/mo)', 'Full Holdings Insight', 'Dividend Tracker', 'ETF Overlap', 'Full Benchmark', 'Trade Simulator', 'Score History'],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Premium',
    price: 65,
    features: ['100 portfolio positions', 'Portfolio Builder (100 positions)', 'Alpha Chat (100/day)', 'Alpha Workspace (40 conversations/day · 1,000/mo)', 'Alpha Memory + Live Research', 'Unlimited Correlations', 'Portfolio regime-shift alerts', 'AI Stock Thesis', 'Earnings Summaries', 'Everything in Analyst'],
    cta: 'Start Free Trial',
    popular: false,
  },
];

const COMPARISON_ROWS = [
  { feature: 'Portfolio Analysis',      free: '5 positions',   core: '15 positions',    analyst: '30 positions',    premium: '100 positions' },
  { feature: 'Portfolio Builder',       free: '5 positions',   core: '15 positions',    analyst: '30 positions',    premium: '100 positions' },
  { feature: 'Portfolio analyses / month',      free: '3',             core: '100',             analyst: 'Unlimited',       premium: 'Unlimited' },
  { feature: 'Stock Screener',          free: '5/day',         core: '50/day',          analyst: 'Unlimited',       premium: 'Unlimited' },
  { feature: 'Grade-change alerts',     free: '1',             core: '20',              analyst: '50',              premium: '100' },
  { feature: 'Market Trends',           free: '✓',             core: '✓',               analyst: '✓',               premium: '✓' },
  { feature: 'Market Heatmap',          free: '✓',             core: '✓',               analyst: '✓',               premium: '✓' },
  { feature: 'Moving Averages',         free: '✓',             core: '✓',               analyst: '✓',               premium: '✓' },
  { feature: 'Macro Energy',            free: '✓',             core: '✓',               analyst: '✓',               premium: '✓' },
  { feature: 'Holdings Insight',        free: '—',             core: 'P&L only',        analyst: 'Full',            premium: 'Full' },
  { feature: 'Benchmark Comparison',    free: '—',             core: 'Basic',           analyst: 'Full',            premium: 'Full' },
  { feature: 'PDF Export',              free: '—',             core: '✓',               analyst: '✓',               premium: '✓' },
  { feature: 'Dividend Tracker',        free: '—',             core: '—',               analyst: '✓',               premium: '✓' },
  { feature: 'ETF Overlap',             free: '—',             core: '—',               analyst: '✓',               premium: '✓' },
  { feature: 'Trade Simulator',         free: '—',             core: '—',               analyst: '✓',               premium: '✓' },
  { feature: 'Score History',           free: '—',             core: '—',               analyst: '✓',               premium: '✓' },
  { feature: 'Alpha Chat (bubble)',     free: '3/day',         core: '10/day',          analyst: '25/day',          premium: '100/day' },
  { feature: 'Alpha Workspace conversations', free: '—',             core: '5/mo',            analyst: '25/mo',           premium: '40/day · 1k/mo' },
  { feature: 'Correlations',            free: '—',             core: '3 tickers/mo',    analyst: '15 tickers/mo',   premium: 'Unlimited' },
  { feature: 'Alpha Memory',            free: '—',             core: '—',               analyst: '—',               premium: '✓' },
  { feature: 'Alpha Live Research',     free: '—',             core: '—',               analyst: '—',               premium: '✓' },
  { feature: 'AI Stock Thesis',         free: '—',             core: '—',               analyst: '—',               premium: '✓' },
  { feature: 'Earnings Summary',        free: '—',             core: '—',               analyst: '—',               premium: '✓' },
  { feature: 'Portfolio regime alerts', free: '—',             core: '—',               analyst: '—',               premium: '✓' },
];

export default function MarketingPage({ onSignIn, onGetStarted }) {

  return (
    <div className="marketing-page">
      {/* Navigation */}
      <nav className="marketing-nav">
        <div className="marketing-nav-brand">
          <span className="marketing-logo">AG</span>
          <span className="marketing-brand-name"><span className="brand-highlight">Alpha</span>Grade</span>
        </div>
        <div className="marketing-nav-links">
          <a href="#features" className="marketing-nav-link">Features</a>
          <a href="#how-it-works" className="marketing-nav-link">How It Works</a>
          <a href="#pricing" className="marketing-nav-link">Pricing</a>
          <a href="#faq" className="marketing-nav-link">FAQ</a>
          <button className="marketing-nav-signin" onClick={onSignIn}>Sign In</button>
          <button className="marketing-nav-cta" onClick={onGetStarted}>Get Started Free</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            You Have the Money to Invest.<br />
            You Refuse to Hand It Over.<br />
            <span className="hero-highlight">Stop Sitting on the Sideline.</span>
          </h1>
          <p className="hero-subtitle">
            AlphaGrade puts the same data institutional investors pay thousands a month for into your hands — wired into an AI thinking partner that knows your portfolio, your risk tolerance, and your investment style. The framework, the data, and the confidence to trust your own process. At a price the retail investor can actually afford.
          </p>
          <div className="hero-buttons">
            <button className="hero-cta-primary" onClick={() => { window.location.href = '/screener'; }}>
              Start grading — free
            </button>
            <button className="hero-cta-secondary" onClick={() => {
              // Compute the target Y manually instead of relying on
              // scrollIntoView. The demos above lazy-load and shift the
              // layout, which was causing the smooth scroll to land past
              // the section's top. Manual scroll with a small offset is
              // robust against that.
              const el = document.getElementById('founder-story');
              if (el) {
                const top = el.getBoundingClientRect().top + window.pageYOffset - 16;
                window.scrollTo({ top, behavior: 'smooth' });
              }
            }}>
              Why I Built This
            </button>
          </div>
          <p className="hero-note">No credit card required. Free plan includes 5 positions.</p>
        </div>
        <div className="hero-visual">
          <HeroGradeWidget onGetStarted={onGetStarted} />
        </div>
      </section>

      {/* Social Proof Bar */}
      <div className="marketing-proof">
        <span>Scores powered by</span>
        <span className="proof-item">Polygon.io</span>
        <span className="proof-divider">•</span>
        <span className="proof-item">Financial Modeling Prep</span>
        <span className="proof-divider">•</span>
        <span className="proof-item">CoinGecko</span>
        <span className="proof-divider">•</span>
        <span className="proof-item">OpenAI</span>
      </div>

      {/* Interactive product tour — replaces the old YouTube walkthroughs.
          Each step plays a code-driven animated demo (see WalkthroughTour). */}
      <section id="walkthroughs" className="marketing-walkthroughs">
        <h2 className="section-title">See How AlphaGrade Works</h2>
        <p className="section-subtitle">A quick interactive tour — step through the core features and watch each one in action.</p>
        <WalkthroughTour />
      </section>

      {/* Portfolio Demo */}
      <section className="marketing-preview">
        <h2 className="section-title">See What a Graded Portfolio Looks Like</h2>
        <p className="section-subtitle">Upload your holdings and watch every position get scored in real-time.</p>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <MobileDemoGate label="portfolio demo" placeholderHeight={380}>
            {({ autoPlay, loop }) => <PortfolioDemo autoPlay={autoPlay} loop={loop} />}
          </MobileDemoGate>
        </div>
      </section>

      {/* Screener Demo */}
      <section className="marketing-preview screener-preview">
        <h2 className="section-title">Screen Any Stock in Seconds</h2>
        <p className="section-subtitle">Enter any tickers and get instant conviction scores with detailed breakdowns.</p>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <MobileDemoGate label="screener demo" placeholderHeight={420}>
            {({ autoPlay, loop }) => <ScreenerDemo autoPlay={autoPlay} loop={loop} />}
          </MobileDemoGate>
        </div>
      </section>

      {/* Market Trends Demo */}
      <section className="marketing-preview trends-preview">
        <h2 className="section-title">Stay Ahead of the Market</h2>
        <p className="section-subtitle">Real-time market intelligence — heatmaps, moving averages, sector performance, and more. Free for all users.</p>
        <div style={{ maxWidth: 650, margin: '0 auto' }}>
          <MobileDemoGate label="market trends demo" placeholderHeight={400}>
            {({ autoPlay, loop }) => <MarketTrendsDemo autoPlay={autoPlay} loop={loop} />}
          </MobileDemoGate>
        </div>
      </section>

      {/* Founder Story Section */}
      <section id="founder-story" className="marketing-founder">
        <div className="founder-content">
          <h2 className="section-title">Why I Built AlphaGrade</h2>
          <p className="founder-pull">
            "I didn't have any tools to tell me if what I was thinking was how a savvy investor would think."
          </p>
          <div className="founder-narrative">
            <p>
              When the pandemic hit, I told my business partner we should buy airlines, cruise lines, and hotels at the bottom and wait for the rebound. He had the higher risk tolerance — he did it that night on his Robinhood account and made money. <strong>I sat on the sideline.</strong> Not because the idea was wrong. Because I didn't have any tools to tell me if what I was thinking was how a savvy investor would think.
            </p>
            <p>
              That's why I built AlphaGrade.
            </p>
            <p>
              I have other businesses. I have money I want to grow. But I'm not going to hand it to an advisor who charges fees to do what I can do myself, and I'm not going to wing it on a Robinhood account without understanding what makes a stock a good or bad purchase. There's a huge group of people in the middle — smart professionals who manage their own money but don't trust their gut alone.
            </p>
            <p>
              <strong>AlphaGrade is for them.</strong> Institutional research data, an AI thinking partner that knows your portfolio, and a transparent scoring engine that shows you exactly why a stock looks good or bad. So you can stop sitting on the sideline.
            </p>
          </div>
          <p className="founder-signature">— Chris, founder of AlphaGrade</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="marketing-features">
        <h2 className="section-title">What You Get</h2>
        <p className="section-subtitle">The institutional data, a transparent scoring engine, and an AI research partner that ties it all together — your portfolio, your scores, and your correlations in one place.</p>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <span className="feature-icon">{f.icon}</span>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.description}</p>
              <span className="feature-tier">{f.tier}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="marketing-steps">
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">Three steps to a fully graded portfolio.</p>
        <div className="steps-grid">
          {STEPS.map((s, i) => (
            <div key={i} className="step-card">
              <span className="step-number">{s.number}</span>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="marketing-pricing">
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <p className="section-subtitle">Start free. Upgrade when you're ready.</p>
        <div className="pricing-cards">
          {TIERS.map((t, i) => (
            <div key={i} className={`mkt-pricing-card ${t.popular ? 'popular' : ''}`}>
              {t.popular && <span className="mkt-popular-badge">Most Popular</span>}
              <h3 className="mkt-tier-name">{t.name}</h3>
              <div className="mkt-price">
                <span className="mkt-price-amount">${t.price}</span>
                {t.price > 0 && <span className="mkt-price-period">/mo</span>}
              </div>
              <ul className="mkt-features">
                {t.features.map((f, j) => (
                  <li key={j}>{f}</li>
                ))}
              </ul>
              <button className="mkt-pricing-cta" onClick={onGetStarted}>{t.cta}</button>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="marketing-comparison">
        <h2 className="section-title">Compare All Features</h2>
        <div className="comparison-table-wrap">
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="ct-feature">Feature</th>
                <th className="ct-tier">Free</th>
                <th className="ct-tier">Core<br/><span className="ct-price">$10.99/mo</span></th>
                <th className="ct-tier ct-popular">Analyst<br/><span className="ct-price">$24/mo</span></th>
                <th className="ct-tier">Premium<br/><span className="ct-price">$65/mo</span></th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={i}>
                  <td className="ct-feature-name">{row.feature}</td>
                  <td className={`ct-val ${row.free === '—' ? 'ct-no' : row.free === '✓' ? 'ct-yes' : ''}`}>{row.free}</td>
                  <td className={`ct-val ${row.core === '—' ? 'ct-no' : row.core === '✓' ? 'ct-yes' : ''}`}>{row.core}</td>
                  <td className={`ct-val ct-pop-col ${row.analyst === '—' ? 'ct-no' : row.analyst === '✓' ? 'ct-yes' : ''}`}>{row.analyst}</td>
                  <td className={`ct-val ${row.premium === '—' ? 'ct-no' : row.premium === '✓' ? 'ct-yes' : ''}`}>{row.premium}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Final CTA */}
      {/* FAQ Section */}
      <section id="faq" className="marketing-faq">
        <div className="marketing-section-header">
          <h2>Frequently Asked Questions</h2>
          <p>Everything you need to know before getting started.</p>
        </div>
        <div className="marketing-faq-container">
          <FAQAccordion items={MARKETING_FAQS} variant="marketing" defaultOpenIndex={0} />
        </div>
      </section>

      <section className="marketing-final-cta">
        <h2>Ready to Grade Your Portfolio?</h2>
        <p>Join AlphaGrade and get conviction-backed insights on every position you hold.</p>
        <button className="hero-cta-primary" onClick={onGetStarted}>Get Started Free</button>
      </section>

      {/* Footer */}
      <footer className="marketing-footer">
        <div className="footer-brand">
          <span className="marketing-logo">AG</span>
          <span className="marketing-brand-name"><span className="brand-highlight">Alpha</span>Grade</span>
        </div>
        <p className="footer-disclaimer">
          AlphaGrade is for educational and informational purposes only and does not constitute investment advice.
          Past performance is not indicative of future results. All investments involve risk.
        </p>
        <p className="footer-legal-links" style={{ marginBottom: '0.75rem' }}>
          <a href="/privacy">Privacy Policy</a>
          <span style={{ margin: '0 0.5rem', opacity: 0.5 }}>·</span>
          <a href="/terms">Terms of Service</a>
        </p>
        <p className="footer-copy">© 2026 AlphaGrade. All rights reserved.</p>
      </footer>

      <BackToTop />
    </div>
  );
}
